import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SeriesCard from '../components/SeriesCard';
import { useLanguage } from '../context/LanguageContext';
import { tvApi, languageToTMDB } from '../services/api';
import { ArrowLeft, Tv, Calendar, Clock, Star, Users, Play } from 'lucide-react';

const SeriesDetailPage = () => {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const [series, setSeries] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSeason, setSelectedSeason] = useState(null);

  useEffect(() => {
    const fetchSeries = async () => {
      try {
        setLoading(true);
        const tmdbLang = languageToTMDB[language] || 'en-US';
        
        const [seriesData, similarData] = await Promise.all([
          tvApi.getDetail(parseInt(id), tmdbLang),
          tvApi.getSimilar(parseInt(id), 1, tmdbLang)
        ]);
        
        setSeries(seriesData);
        setSimilar(similarData.results || []);
        
        // Set first season as default
        if (seriesData.seasons && seriesData.seasons.length > 0) {
          const firstRealSeason = seriesData.seasons.find(s => s.season_number > 0) || seriesData.seasons[0];
          setSelectedSeason(firstRealSeason);
        }
      } catch (error) {
        console.error('Error fetching series:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSeries();
  }, [id, language]);

  if (loading) {
    return (
      <div className="bg-[#0B0F0E] min-h-screen">
        <Header />
        <main className="pt-32 pb-20">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
            <div className="animate-pulse">
              <div className="h-8 bg-[#1a2420] rounded w-32 mb-8" />
              <div className="flex flex-col md:flex-row gap-8">
                <div className="w-64 aspect-[2/3] bg-[#1a2420] rounded-lg" />
                <div className="flex-1 space-y-4">
                  <div className="h-10 bg-[#1a2420] rounded w-3/4" />
                  <div className="h-6 bg-[#1a2420] rounded w-1/2" />
                  <div className="h-32 bg-[#1a2420] rounded" />
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!series) {
    return (
      <div className="bg-[#0B0F0E] min-h-screen">
        <Header />
        <main className="pt-32 pb-20">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%] text-center">
            <h1 className="text-4xl font-semibold text-[#E6FFF6] mb-4">
              {t('series.notFound') || 'Series Not Found'}
            </h1>
            <Link to="/series" className="text-[#52ea88] hover:underline">
              {t('series.backToSeries') || 'Back to TV Series'}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#0B0F0E] min-h-screen">
      <Header />

      {/* Backdrop */}
      {series.backdrop && (
        <div className="absolute top-0 left-0 right-0 h-[500px] overflow-hidden">
          <img
            src={series.backdrop}
            alt={series.name}
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F0E]/50 via-[#0B0F0E]/80 to-[#0B0F0E]" />
        </div>
      )}

      <main className="relative pt-32 pb-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
          {/* Back button */}
          <Link
            to="/series"
            className="inline-flex items-center gap-2 text-[#52ea88] hover:text-[#E6FFF6] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('series.backToSeries') || 'Back to TV Series'}
          </Link>

          {/* Series Info */}
          <div className="flex flex-col md:flex-row gap-8">
            {/* Poster */}
            <div className="md:w-80 flex-shrink-0">
              <div className="aspect-[2/3] rounded-lg overflow-hidden bg-[#1a2420] shadow-2xl">
                {series.poster ? (
                  <img
                    src={series.poster}
                    alt={series.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Tv className="w-24 h-24 text-[#52ea88]/30" />
                  </div>
                )}
              </div>
            </div>

            {/* Details */}
            <div className="flex-1">
              {/* TV Badge */}
              <span className="inline-block bg-[#52ea88] text-[#0B0F0E] text-xs font-semibold px-3 py-1 rounded mb-4">
                TV SERIES
              </span>

              <h1 className="text-4xl md:text-5xl font-semibold text-[#E6FFF6] mb-2">
                {series.name}
              </h1>
              
              {series.tagline && (
                <p className="text-xl text-[#52ea88] italic mb-4">"{series.tagline}"</p>
              )}

              {/* Meta info */}
              <div className="flex flex-wrap gap-4 mb-6 text-[#9FBFB4]">
                {series.first_air_date && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#52ea88]" />
                    <span>{series.year}</span>
                  </div>
                )}
                {series.number_of_seasons && (
                  <div className="flex items-center gap-2">
                    <Play className="w-4 h-4 text-[#52ea88]" />
                    <span>{series.number_of_seasons} {series.number_of_seasons === 1 ? 'Season' : 'Seasons'}</span>
                  </div>
                )}
                {series.number_of_episodes && (
                  <div className="flex items-center gap-2">
                    <Tv className="w-4 h-4 text-[#52ea88]" />
                    <span>{series.number_of_episodes} Episodes</span>
                  </div>
                )}
                {series.episode_run_time && series.episode_run_time.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#52ea88]" />
                    <span>{series.episode_run_time[0]} min</span>
                  </div>
                )}
                {series.vote_average > 0 && (
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-[#52ea88] fill-[#52ea88]" />
                    <span>{series.vote_average.toFixed(1)}</span>
                  </div>
                )}
              </div>

              {/* Genres */}
              {series.genres && series.genres.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {series.genres.map((genre) => (
                    <span
                      key={genre.id}
                      className="px-3 py-1 bg-[#52ea88]/10 text-[#52ea88] text-sm rounded-full border border-[#52ea88]/30"
                    >
                      {genre.name}
                    </span>
                  ))}
                </div>
              )}

              {/* Networks/Streaming Services */}
              {series.networks && series.networks.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm text-[#9FBFB4] mb-2">{t('series.availableOn') || 'Available on'}:</p>
                  <div className="flex flex-wrap gap-3">
                    {series.networks.map((network) => (
                      <div
                        key={network.id}
                        className="flex items-center gap-2 px-3 py-2 bg-[#1a2420] rounded-lg"
                      >
                        {network.logo && (
                          <img src={network.logo} alt={network.name} className="h-6 object-contain" />
                        )}
                        <span className="text-sm text-[#E6FFF6]">{network.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Creators */}
              {series.creators && series.creators.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm text-[#9FBFB4] mb-1">{t('series.createdBy') || 'Created by'}:</p>
                  <p className="text-[#E6FFF6]">{series.creators.join(', ')}</p>
                </div>
              )}

              {/* Overview */}
              {series.overview && (
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-[#E6FFF6] mb-3">
                    {t('series.overview') || 'Overview'}
                  </h2>
                  <p className="text-[#9FBFB4] leading-relaxed">{series.overview}</p>
                </div>
              )}

              {/* Status */}
              {series.status && (
                <div className="inline-block px-3 py-1 bg-[#1a2420] rounded text-sm">
                  <span className="text-[#9FBFB4]">{t('series.status') || 'Status'}: </span>
                  <span className="text-[#E6FFF6]">{series.status}</span>
                </div>
              )}
            </div>
          </div>

          {/* Cast */}
          {series.cast && series.cast.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-semibold text-[#E6FFF6] mb-6 flex items-center gap-3">
                <Users className="w-6 h-6 text-[#52ea88]" />
                {t('series.cast') || 'Cast'}
              </h2>
              <div className="flex gap-4 overflow-x-auto pb-4">
                {series.cast.map((person) => (
                  <Link
                    key={person.id}
                    to={`/stars/${person.id}`}
                    className="flex-shrink-0 w-32 group"
                  >
                    <div className="aspect-[2/3] rounded-lg overflow-hidden bg-[#1a2420]">
                      {person.profile_path ? (
                        <img
                          src={person.profile_path}
                          alt={person.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Users className="w-8 h-8 text-[#52ea88]/30" />
                        </div>
                      )}
                    </div>
                    <div className="mt-2">
                      <p className="text-sm font-medium text-[#E6FFF6] group-hover:text-[#52ea88] transition-colors line-clamp-1">
                        {person.name}
                      </p>
                      {person.character && (
                        <p className="text-xs text-[#9FBFB4] line-clamp-1">{person.character}</p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Seasons */}
          {series.seasons && series.seasons.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-semibold text-[#E6FFF6] mb-6 flex items-center gap-3">
                <Play className="w-6 h-6 text-[#52ea88]" />
                {t('series.seasons') || 'Seasons'}
              </h2>
              
              {/* Season tabs */}
              <div className="flex gap-2 overflow-x-auto pb-4 mb-6">
                {series.seasons.filter(s => s.season_number > 0).map((season) => (
                  <button
                    key={season.id}
                    onClick={() => setSelectedSeason(season)}
                    className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedSeason?.id === season.id
                        ? 'bg-[#52ea88] text-[#0B0F0E]'
                        : 'bg-[#1a2420] text-[#9FBFB4] hover:text-[#E6FFF6]'
                    }`}
                  >
                    {season.name}
                  </button>
                ))}
              </div>

              {/* Selected season info */}
              {selectedSeason && (
                <div className="flex flex-col md:flex-row gap-6 p-6 bg-[#121917] rounded-lg border border-[#52ea88]/10">
                  {selectedSeason.poster && (
                    <div className="md:w-40 flex-shrink-0">
                      <img
                        src={selectedSeason.poster}
                        alt={selectedSeason.name}
                        className="w-full rounded-lg"
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-[#E6FFF6] mb-2">
                      {selectedSeason.name}
                    </h3>
                    <div className="flex gap-4 text-sm text-[#9FBFB4] mb-4">
                      {selectedSeason.air_date && (
                        <span>{selectedSeason.air_date.substring(0, 4)}</span>
                      )}
                      {selectedSeason.episode_count && (
                        <span>{selectedSeason.episode_count} Episodes</span>
                      )}
                    </div>
                    {selectedSeason.overview && (
                      <p className="text-[#9FBFB4] leading-relaxed">{selectedSeason.overview}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Similar Series */}
          {similar.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-semibold text-[#E6FFF6] mb-6">
                {t('series.similarShows') || 'Similar Shows'}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {similar.slice(0, 6).map((show) => (
                  <SeriesCard key={show.id} series={show} showRating={false} />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SeriesDetailPage;
